import { useMemo, useCallback, useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  IconButton,
  Icon,
  Menu,
  Portal,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiCornerDownLeft, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { getStepNumber, getElementSummary, getRejoinTargets } from './laneUtils'
import { getBranchDestination } from './branchDestination'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ScriptElementType, ELEMENT_TYPE_LABELS } from '../../types/script'
import type {
  ScriptElementFormState,
  ScriptLane,
  TagPickerDto,
  QuestionPickerDto,
} from '../../types/script'
import { LaneElementCard } from './LaneElementCard'
import { BranchSection } from './BranchSection'

interface BranchLaneProps {
  branch: ScriptLane
  lanes: ScriptLane[]
  onAddElement: (laneId: string, type: ScriptElementType) => void
  onUpdateElement: (laneId: string, clientId: string, updates: Partial<ScriptElementFormState>) => void
  onRemoveElement: (laneId: string, clientId: string) => void
  onCreateBranch: (sourceClientId: string, branchType: string, branchKey: string, label: string) => void
  onDeleteLane: (laneId: string) => void
  onSetMergeTarget: (laneId: string, targetClientId: string | undefined) => void
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
}

export function BranchLane({
  branch,
  lanes,
  onAddElement,
  onUpdateElement,
  onRemoveElement,
  onCreateBranch,
  onDeleteLane,
  onSetMergeTarget,
  tagOptions,
  questionOptions,
}: BranchLaneProps) {
  // Memoize sortable items to prevent SortableContext from resetting on every render
  const sortableItems = useMemo(
    () => branch.elements.map((el) => el.clientId),
    [branch.elements]
  )

  // Memoize the add element handler to prevent MenuItem re-renders
  const handleAddElementClick = useCallback(
    (type: ScriptElementType) => {
      onAddElement(branch.id, type)
    },
    [branch.id, onAddElement]
  )

  // Potential rejoin targets: main-flow elements after this branch tree's
  // main-flow origin. Handles arbitrary nesting depth (see getRejoinTargets).
  const rejoinTargets = useMemo(
    () => getRejoinTargets(lanes, branch),
    [lanes, branch]
  )

  // Memoize branch lookup map for nested branches (branches originating from elements in this lane)
  const branchesBySourceId = useMemo(() => {
    const map = new Map<string, ScriptLane[]>()
    for (const lane of lanes) {
      if (lane.id === 'main' || !lane.sourceElementClientId) continue
      const existing = map.get(lane.sourceElementClientId) || []
      map.set(lane.sourceElementClientId, [...existing, lane])
    }
    return map
  }, [lanes])

  const [open, setOpen] = useState(true)
  const destination = getBranchDestination(branch, lanes)

  return (
    <Box
      data-testid="branch-lane"
      data-lane-id={branch.id}
      borderWidth="1px"
      borderColor={`${branch.colorScheme}.300`}
      borderRadius="lg"
      bg={`${branch.colorScheme}.50`}
      overflow="hidden"
    >
      {/* Collapsible header */}
      <HStack
        as="div"
        role="button"
        tabIndex={0}
        data-testid="branch-lane-header"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen(!open)
          }
        }}
        cursor="pointer"
        w="full"
        px={3}
        py={2}
        gap={2}
        bg={`${branch.colorScheme}.100`}
        borderBottomWidth={open ? '1px' : 0}
        borderBottomColor={`${branch.colorScheme}.200`}
        textAlign="left"
      >
        <Icon as={open ? FiChevronDown : FiChevronRight} color={`${branch.colorScheme}.500`} boxSize={3} />
        <Badge colorPalette={branch.colorScheme} fontSize="2xs" variant="solid">{branch.label}</Badge>
        <Text fontSize="xs" color="gray.500">
          {branch.elements.length} {branch.elements.length === 1 ? 'step' : 'steps'}
        </Text>
        <Text fontSize="xs" color="gray.400" ml="auto" lineClamp={1}>
          {destination.kind === 'rejoin' ? `Rejoins #${destination.stepNumber ?? '?'}` : 'Ends script'}
        </Text>
        <IconButton
          aria-label="Delete branch"
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={(e) => {
            e.stopPropagation()
            if (
              window.confirm(
                `Delete "${branch.label}" branch and its ${branch.elements.length} element(s)?`
              )
            ) {
              onDeleteLane(branch.id)
            }
          }}><FiTrash2 /></IconButton>
      </HStack>

      {open && (
        <Box>
          {/* Sortable element list */}
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            <VStack gap={2} p={2} align="stretch" minH="60px" pl={3}>
              {branch.elements.length === 0 && (
                <Box py={4} textAlign="center">
                  <Text fontSize="sm" color="gray.400">
                    Add elements to this branch path.
                  </Text>
                </Box>
              )}

              {branch.elements.map((element, idx) => {
                const stepNumber = getStepNumber(lanes, element.clientId) ?? 0
                const nestedBranches = branchesBySourceId.get(element.clientId) || []

                return (
                  <Box key={element.clientId}>
                    <LaneElementCard
                      element={element}
                      lanes={lanes}
                      stepNumber={stepNumber}
                      isLast={idx === branch.elements.length - 1 && nestedBranches.length === 0}
                      laneColorScheme={branch.colorScheme}
                      onUpdate={(updates) =>
                        onUpdateElement(branch.id, element.clientId, updates)
                      }
                      onRemove={() => onRemoveElement(branch.id, element.clientId)}
                      onCreateBranch={(branchType, branchKey, label) =>
                        onCreateBranch(element.clientId, branchType, branchKey, label)
                      }
                      tagOptions={tagOptions}
                      questionOptions={questionOptions}
                    />

                    {/* Render nested branches if this element has any */}
                    {nestedBranches.length > 0 && (
                      <BranchSection
                        branches={nestedBranches}
                        lanes={lanes}
                        onAddElement={onAddElement}
                        onUpdateElement={onUpdateElement}
                        onRemoveElement={onRemoveElement}
                        onCreateBranch={onCreateBranch}
                        onDeleteLane={onDeleteLane}
                        onSetMergeTarget={onSetMergeTarget}
                        tagOptions={tagOptions}
                        questionOptions={questionOptions}
                      />
                    )}
                  </Box>
                )
              })}
            </VStack>
          </SortableContext>

          {/* Add element button */}
          <Box px={2} pb={2} pl={3}>
            <Menu.Root
              closeOnSelect={true}
              positioning={{
                placement: 'bottom',
                strategy: 'fixed',
                flip: true
              }}>
              <Menu.Trigger asChild><Button size="xs" variant="ghost" colorPalette={branch.colorScheme} w="full"><FiPlus />Add Step
                                </Button></Menu.Trigger>
              <Portal><Menu.Positioner><Menu.Content>
                    {Object.entries(ELEMENT_TYPE_LABELS).map(([typeStr, label]) => (
                      <Menu.Item
                        key={typeStr}
                        fontSize="sm"
                        onSelect={() => handleAddElementClick(typeStr as ScriptElementType)}
                        value={typeStr}>
                        {label}
                      </Menu.Item>
                    ))}
                  </Menu.Content></Menu.Positioner></Portal>
            </Menu.Root>
          </Box>

          {/* Rejoin control section */}
          <Box px={2} pb={2} pl={3}>
            {branch.mergeTargetClientId ? (
              // Rejoin is set - show target with clear button
              (<VStack gap={1} align="stretch">
                <HStack
                  px={2}
                  py={1.5}
                  bg={`${branch.colorScheme}.50`}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={`${branch.colorScheme}.200`}
                  gap={2}
                  justify="space-between"
                >
                  <HStack gap={1} flex={1} minW={0}>
                    <Icon as={FiCornerDownLeft} boxSize={3} color={`${branch.colorScheme}.500`} />
                    <Text fontSize="xs" fontWeight="medium" color={`${branch.colorScheme}.700`} lineClamp={1}>
                      Rejoins at Step {getStepNumber(lanes, branch.mergeTargetClientId)}
                    </Text>
                  </HStack>
                  <IconButton
                    aria-label="Clear rejoin"
                    size="xs"
                    variant="ghost"
                    colorPalette="gray"
                    onClick={() => onSetMergeTarget(branch.id, undefined)}><FiX /></IconButton>
                </HStack>
              </VStack>)
            ) : (
              // No rejoin - show "End of script" with set rejoin button
              (<VStack gap={1} align="stretch">
                <HStack
                  px={2}
                  py={1.5}
                  bg="gray.100"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.300"
                  gap={2}
                  justify="space-between"
                >
                  <Text fontSize="xs" fontWeight="medium" color="gray.600">
                    End of script
                  </Text>
                  {rejoinTargets.length > 0 && (
                    <Menu.Root positioning={{
                      placement: 'top'
                    }}>
                      <Menu.Trigger asChild><Button size="xs" variant="ghost" colorPalette="gray" fontSize="xs">Set Rejoin
                                                <FiCornerDownLeft /></Button></Menu.Trigger>
                      <Portal><Menu.Positioner><Menu.Content>
                            {rejoinTargets.map(({ element, stepNumber }) => (
                              <Menu.Item
                                key={element.clientId}
                                fontSize="sm"
                                onSelect={() => onSetMergeTarget(branch.id, element.clientId)}
                                value={element.clientId}>
                                <VStack align="start" gap={0}>
                                  <Text>Step {stepNumber}</Text>
                                  <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                    {getElementSummary(element, { tagOptions, questionOptions })}
                                  </Text>
                                </VStack>
                              </Menu.Item>
                            ))}
                          </Menu.Content></Menu.Positioner></Portal>
                    </Menu.Root>
                  )}
                </HStack>
              </VStack>)
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
